import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { supabase, readableError } from '../lib/supabase'
import { useSession } from '../lib/session'
import { PENDING_INVITE_KEY } from '../lib/invite'
import { Shell } from '../components/Shell'
import { Sill, Spinner } from '../components/ui'
import Auth from './Auth'

const STAFF_SEAT_LABEL = {
  admin: 'an administrator', reviewer: 'a reviewer', songwriter: 'a songwriter',
}

const RESULT_COPY = {
  referral: () => "You're in.",
  cowriter: (r) => `You're writing "${r.song_title}" with ${r.inviter_first_name}.`,
  circle: (r) => `You're in ${r.inviter_first_name}'s "${r.circle_name}".`,
  church_staff: (r) => (r.staff_role === 'songwriter'
    ? `You write with ${r.church_name}.`
    : `You're ${STAFF_SEAT_LABEL[r.staff_role] || 'on staff'} at ${r.church_name}.`),
}

/** The seat that redeeming the invite just granted decides what's actually
 *  useful to say next — a reviewer's queue and an admin's setup screen are
 *  different jobs, so the orientation line shouldn't pretend they're one. */
const RESULT_SUB = {
  church_staff: (r) => ({
    admin: "You can bring on the rest of the team, set your church's review process, and manage endorsements.",
    reviewer: 'Songs your church endorses land in your inbox for review — that queue is waiting whenever you are.',
    // A writer isn't being given a job, they're being told the door is open —
    // they were already approved by the invite, so there is nothing to wait for.
    songwriter: `${r.church_name} has already approved you, so there's no waiting. Write something and send it to them whenever it's ready.`,
  }[r.staff_role]),
}

/**
 * The landing spot for a code, whether it arrived as a link or a scanned QR.
 * Three states, not one screen: no session yet (preview, then sign in in
 * place), a session with the profile still unfinished (send them to Welcome,
 * remembering the code for after), or a session ready to redeem.
 */
export default function InviteRedeem() {
  const { code } = useParams()
  const nav = useNavigate()
  const { loading, user, profile, refresh } = useSession()

  const [preview, setPreview] = useState(null)
  const [result, setResult] = useState(null)
  const [busy, setBusy] = useState(true)
  const [err, setErr] = useState(null)

  const profileIncomplete = user && (!profile?.first_name || !user.user_metadata?.lane_set)

  // No session, or a session still mid-signup: hold the code for Gate to pick
  // back up once the profile is finished.
  useEffect(() => {
    if (loading) return
    if (!user || profileIncomplete) localStorage.setItem(PENDING_INVITE_KEY, code)
    if (profileIncomplete) nav('/welcome', { replace: true })
  }, [loading, user, profileIncomplete, code, nav])

  useEffect(() => {
    if (loading || !user || profileIncomplete) return
    let alive = true
    setBusy(true)
    supabase.rpc('redeem_invite_code', { p_code: code }).then(async ({ data, error }) => {
      if (!alive) return
      if (error) { setBusy(false); return setErr(readableError(error)) }
      localStorage.removeItem(PENDING_INVITE_KEY)
      // A church_staff redemption just changed which role Gate would send this
      // session to — without this, "Continue" would land them on the
      // songwriter home feed until their next full sign-in, not the inbox or
      // admin screen the seat they were just given actually opens onto.
      await refresh()
      if (!alive) return
      setBusy(false)
      setResult(data)
    })
    return () => { alive = false }
  }, [loading, user, profileIncomplete, code, refresh])

  // Before sign-in: enough to say who this is from, without exposing what
  // it's for — that's the whole reason preview_invite_code returns so little.
  useEffect(() => {
    if (user) return
    supabase.rpc('preview_invite_code', { p_code: code }).then(({ data }) => setPreview(data))
  }, [user, code])

  if (loading || profileIncomplete) return <Shell><Spinner /></Shell>

  if (!user) {
    return (
      <>
        {preview && (
          <div className="tile" style={{ maxWidth: 480, margin: '0 auto 0' }}>
            <p className="small">
              {preview.inviter_first_name || 'A writer'} invited you to Beseter.
            </p>
          </div>
        )}
        <Auth />
      </>
    )
  }

  return (
    <Shell tabs>
      <div className="eyebrow">Invite</div>
      {busy && <><h2 className="q">One moment.</h2><Sill /><Spinner /></>}
      {!busy && err && (<>
        <h2 className="q">That link didn't work.</h2>
        <p className="sub">{err}</p>
        <Sill />
        <button className="btn btn-primary" onClick={() => nav('/')}>Continue to Beseter</button>
      </>)}
      {!busy && result && (<>
        <h2 className="q">{RESULT_COPY[result.kind](result)}</h2>
        {RESULT_SUB[result.kind] && <p className="sub">{RESULT_SUB[result.kind](result)}</p>}
        <Sill />
        <button className="btn btn-primary" onClick={() => nav('/')}>Continue</button>
      </>)}
    </Shell>
  )
}
